import { auth } from './auth';
import { i18n } from './i18n';

/** The reader's language, so the API answers in it (report captions, document 15 section 5.4). */
const language = (): string => i18n.language || 'en';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

/** Fetch wrapper: bearer token, JSON, and the API's { error: { code, message } } envelope. */
export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await auth.getAccessToken();
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: {
      // Fastify rejects an empty body declared as JSON, so only bodies get a content type
      ...(init.body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      'accept-language': language(),
      ...(init.headers ?? {}),
    },
  });
  if (res.status === 401 && auth.enabled) {
    // Missing or rejected token (expired session, restarted IdP): sign in again, a demo visitor
    // as the same demo persona.
    const demo = await auth.wasDemo();
    await auth.manager?.removeUser();
    await (demo ? auth.signInDemo(demo) : auth.signIn());
  }
  if (res.status === 204) return undefined as T;
  const body = (await res.json().catch(() => ({}))) as {
    error?: { code?: string; message?: string; details?: unknown };
  };
  if (!res.ok) {
    throw new ApiError(
      res.status,
      body.error?.code ?? 'HTTP',
      body.error?.message ?? res.statusText,
      body.error?.details,
    );
  }
  return body as T;
}

/**
 * Downloads a file the API serves as an attachment (Excel, CSV, PDF) through the browser, named
 * as the content-disposition header says.
 */
export async function apiDownload(path: string, fallbackName: string): Promise<string> {
  const token = await auth.getAccessToken();
  const res = await fetch(`/api${path}`, {
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      'accept-language': language(),
    },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      error?: { code?: string; message?: string };
    };
    throw new ApiError(
      res.status,
      body.error?.code ?? 'HTTP',
      body.error?.message ?? res.statusText,
    );
  }
  const blob = await res.blob();
  const name =
    /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return name;
}

/** Fetches a binary file (a PDF) with the bearer token; errors use the JSON envelope. */
export async function apiBlob(path: string, init: RequestInit = {}): Promise<Blob> {
  const token = await auth.getAccessToken();
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: {
      ...(init.body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      error?: { code?: string; message?: string };
    };
    throw new ApiError(
      res.status,
      body.error?.code ?? 'HTTP',
      body.error?.message ?? res.statusText,
    );
  }
  return res.blob();
}
